import { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Switch } from "@nexus/ui"
import {
  MAX_FATIGUE_KPI_SELECTION,
  MY_BRAND_DRAWER_FATIGUE_CATALOG,
  type FatigueGranularity,
  isMonthlyGranularityDisabled,
} from "../../lib/my-brand-drawer-fatigue-catalog"
import type { PerformanceKpiId } from "../../lib/my-brand-drawer-performance-catalog"
import { fatigueKpiColorVar } from "../../lib/my-brand-drawer-fatigue-colors"
import { useKpiCatalog } from "@/features/overview/hooks/useKpiCatalog"
import {
  buildFatigueChartRows,
  parseFatigueSeriesRows,
} from "../../lib/my-brand-drawer-fatigue-model"
import { DrilldownMyBrandCreativeFatigueChart } from "./DrilldownMyBrandCreativeFatigueChart"

interface DrilldownMyBrandCreativeFatigueTabProps {
  seriesRows: Array<Record<string, unknown>> | null
  isLoading: boolean
  isError: boolean
  /** Inclusive day count of the selected window — decides which granularities it supports. */
  spanDays: number
  selectedMetrics: PerformanceKpiId[]
  onSelectedMetricsChange: (ids: PerformanceKpiId[]) => void
  granularity: FatigueGranularity
  onGranularityChange: (g: FatigueGranularity) => void
  showPreviousPeriod: boolean
  onShowPreviousPeriodChange: (v: boolean) => void
}

const GRANULARITY_OPTIONS: FatigueGranularity[] = ["daily", "weekly", "monthly"]

export function DrilldownMyBrandCreativeFatigueTab({
  seriesRows,
  isLoading,
  isError,
  spanDays,
  selectedMetrics,
  onSelectedMetricsChange,
  granularity,
  onGranularityChange,
  showPreviousPeriod,
  onShowPreviousPeriodChange,
}: DrilldownMyBrandCreativeFatigueTabProps) {
  const { t } = useTranslation()
  const { myBrandById } = useKpiCatalog("drilldown")
  const monthlyDisabled = isMonthlyGranularityDisabled(spanDays)

  useEffect(() => {
    if (monthlyDisabled && granularity === "monthly") {
      onGranularityChange("weekly")
    }
  }, [monthlyDisabled, granularity, onGranularityChange])

  const labelsById = useMemo(
    () =>
      Object.fromEntries(
        MY_BRAND_DRAWER_FATIGUE_CATALOG.map((entry) => [
          entry.id,
          t(entry.labelKey, entry.defaultLabel),
        ]),
      ) as Record<PerformanceKpiId, string>,
    [t],
  )

  const points = useMemo(
    () => (seriesRows ? parseFatigueSeriesRows(seriesRows) : []),
    [seriesRows],
  )

  const indexed = selectedMetrics.length > 1
  const chartRows = useMemo(
    () => buildFatigueChartRows(points, selectedMetrics, indexed),
    [points, selectedMetrics, indexed],
  )

  const hasPreviousData = useMemo(() => {
    const selectedSuffixes = new Set(
      selectedMetrics
        .map((id) => MY_BRAND_DRAWER_FATIGUE_CATALOG.find((e) => e.id === id)?.sqlSuffix)
        .filter((suffix): suffix is string => Boolean(suffix)),
    )
    return points.some(
      (p) => p.period === "previous" && p.value !== null && selectedSuffixes.has(p.metric),
    )
  }, [points, selectedMetrics])

  const previousToggleDisabled = !hasPreviousData

  const handleToggleMetric = useCallback(
    (id: PerformanceKpiId) => {
      const isSelected = selectedMetrics.includes(id)
      if (isSelected) {
        onSelectedMetricsChange(selectedMetrics.filter((m) => m !== id))
        return
      }
      if (selectedMetrics.length >= MAX_FATIGUE_KPI_SELECTION) {
        toast.message(
          t("drilldown.myBrandDrawer.fatigue.maxSelectionTitle", "Maximum KPIs selected"),
          {
            description: t(
              "drilldown.myBrandDrawer.fatigue.maxSelectionBody",
              "You can compare up to {{count}} KPIs at once.",
              { count: MAX_FATIGUE_KPI_SELECTION },
            ),
          },
        )
        return
      }
      onSelectedMetricsChange([...selectedMetrics, id])
    },
    [onSelectedMetricsChange, selectedMetrics, t],
  )

  return (
    <div className="flex flex-col gap-4 px-6 py-5" aria-busy={isLoading}>
      {isError ? (
        <p className="text-sm text-destructive">
          {t("drilldown.myBrandDrawer.fatigue.loadError", "Failed to load fatigue data.")}
        </p>
      ) : null}

      <div className="rounded-[14px] border border-border/60 bg-muted/30 p-3">
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <span className="w-10 shrink-0 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("drilldown.myBrandDrawer.fatigue.kpiLabel", "KPI")}
            </span>
            <div className="flex flex-wrap gap-2">
              {MY_BRAND_DRAWER_FATIGUE_CATALOG.map((entry) => {
                const selected = selectedMetrics.includes(entry.id)
                const color = fatigueKpiColorVar(entry.id, myBrandById)
                return (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => handleToggleMetric(entry.id)}
                    className={
                      selected
                        ? "inline-flex items-center gap-1.5 rounded-lg border border-primary bg-background px-2.5 py-1 text-xs font-medium text-foreground"
                        : "inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/50"
                    }
                  >
                    <span
                      className="size-2 shrink-0 rounded-full border"
                      style={{
                        background: selected ? color : "transparent",
                        borderColor: color,
                      }}
                    />
                    {labelsById[entry.id]}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="w-10 shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("drilldown.myBrandDrawer.fatigue.periodLabel", "Period")}
            </span>
            <div className="inline-flex rounded-lg border border-border bg-background p-0.5">
              {GRANULARITY_OPTIONS.map((option) => {
                const disabled = option === "monthly" && monthlyDisabled
                const active = granularity === option
                return (
                  <button
                    key={option}
                    type="button"
                    disabled={disabled}
                    aria-disabled={disabled}
                    onClick={() => onGranularityChange(option)}
                    className={
                      active
                        ? "rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium capitalize text-primary"
                        : "rounded-md px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-40"
                    }
                  >
                    {t(`drilldown.myBrandDrawer.fatigue.granularity.${option}`, option)}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-3 bg-foreground/70" />
                {t("drilldown.myBrandDrawer.fatigue.legendCurrent", "Current")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-0 w-3 border-t-2 border-dashed border-foreground/40" />
                {t("drilldown.myBrandDrawer.fatigue.legendPrevious", "Previous")}
              </span>
              <label
                className={
                  previousToggleDisabled
                    ? "inline-flex items-center gap-2 text-xs text-muted-foreground opacity-60"
                    : "inline-flex items-center gap-2 text-xs text-foreground"
                }
                title={
                  previousToggleDisabled
                    ? t(
                        "drilldown.myBrandDrawer.fatigue.noPreviousData",
                        "No previous-period data for the selected KPI.",
                      )
                    : undefined
                }
              >
                <Switch
                  checked={showPreviousPeriod && !previousToggleDisabled}
                  disabled={previousToggleDisabled}
                  aria-disabled={previousToggleDisabled}
                  onCheckedChange={onShowPreviousPeriodChange}
                  aria-label={t(
                    "drilldown.myBrandDrawer.fatigue.vsPreviousPeriod",
                    "vs previous period",
                  )}
                />
                {t("drilldown.myBrandDrawer.fatigue.vsPreviousPeriod", "vs previous period")}
              </label>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <>
          <p className="sr-only">
            {t("drilldown.myBrandDrawer.fatigue.loading", "Loading fatigue chart…")}
          </p>
          <div className="skeleton-shimmer-bar min-h-[340px] rounded-[14px]" />
        </>
      ) : (
        <DrilldownMyBrandCreativeFatigueChart
          rows={chartRows}
          points={points}
          selectedIds={selectedMetrics}
          labelsById={labelsById}
          indexed={indexed}
          showPrevious={showPreviousPeriod && hasPreviousData}
        />
      )}
    </div>
  )
}
